export type BnplMethodType = 'klarna' | 'affirm' | 'afterpay_clearpay';

export type BnplFlags = {
  'bnpl-enabled': boolean;
  'bnpl-expo': boolean;
  'bnpl-klarna': boolean;
  'bnpl-affirm': boolean;
  'bnpl-afterpay': boolean;
};

export const BNPL_METHODS: BnplMethodType[] = [
  'klarna',
  'affirm',
  'afterpay_clearpay',
];

/**
 * How long a pending BNPL session record is kept (SQR-15310).
 *
 * Longer than the 15-minute default the other payment-return flows use: a
 * BNPL payment intent stays pending server-side — locking the cart — until
 * the provider's session expires, which takes hours. The record holds the
 * `paymentIntentId` needed to cancel that intent, so dropping it early would
 * strand the cart with no way to recover it (the customer would have to wait
 * out the provider timeout).
 */
export const BNPL_SESSION_TTL_MS = 24 * 60 * 60 * 1000;

export const MIN_BNPL_AMOUNT_CENTS = 5000;

export const isBnplVisibleForPhase1 = (
  country: string,
  currency: string,
): boolean => {
  return (
    ['us', 'united states'].includes(country.toLowerCase()) &&
    currency.toLowerCase() === 'usd'
  );
};

// Order is intentional: native picks eligibleMethods[0], so klarna > affirm > afterpay
export const getEnabledBnplMethods = (flags: BnplFlags): BnplMethodType[] => {
  const methods: BnplMethodType[] = [];
  if (flags['bnpl-klarna']) methods.push('klarna');
  if (flags['bnpl-affirm']) methods.push('affirm');
  if (flags['bnpl-afterpay']) methods.push('afterpay_clearpay');
  return methods;
};

export const sanitizeBnplCurrency = (
  currency: string | null | undefined,
): string => {
  const normalized = currency?.toLowerCase().trim();
  if (normalized && /^[a-z]{3}$/.test(normalized)) return normalized;
  return 'usd';
};

export const getEligibleBnplMethods = (params: {
  country: string;
  currency: string;
  amountCents: number;
  flags: BnplFlags;
  shop: { afterpayEnabled?: boolean };
}): BnplMethodType[] => {
  const { country, currency, amountCents, flags, shop } = params;

  if (!flags['bnpl-enabled']) return [];
  if (!flags['bnpl-expo']) return [];

  if (!isBnplVisibleForPhase1(country, currency)) return [];
  if (amountCents < MIN_BNPL_AMOUNT_CENTS) return [];

  return getEnabledBnplMethods(flags).filter((method) => {
    if (method === 'afterpay_clearpay' && shop.afterpayEnabled === false) {
      return false;
    }
    return true;
  });
};
